/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useCallback, useDeferredValue } from 'react';

import { useModalRegistry } from './hooks/useModalRegistry';
import { useGlobalKeyboardShortcuts } from './hooks/useGlobalKeyboardShortcuts';
import { useAppDndCoordinator } from './hooks/useAppDndCoordinator';
import { useAppSearch } from './hooks/useAppSearch';
import { useNotifications } from './hooks/useNotifications';
import { useIdeaPool } from './hooks/useIdeaPool';
import { useGanttState } from './hooks/useGanttState';
import { useRecurringTasksScheduler } from './hooks/useRecurringTasksScheduler';
import { useViewNavigationPersistence } from './hooks/useViewNavigationPersistence';
import { useLegacyContactHealing } from './hooks/useLegacyContactHealing';
import { useSidebarPeopleController } from './hooks/useSidebarPeopleController';
import { useAppLifecycleAndSelectors } from './hooks/useAppLifecycleAndSelectors';
import { useAppViewTabsState } from './hooks/useAppViewTabsState';

import { AppSplashLoader, AppWelcomeGate, AppMobileGate } from './components/layout/AppLoadingAndMobileGate';
import { AppDesktopLayout } from './components/layout/AppDesktopLayout';
import { useCommDragMeta } from './components/communication/commDragStore';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { UIProvider, useUI } from './contexts/UIContext';
import { Task } from './types';
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';

export function MainApp({ userId, userEmail }: { userId: string; userEmail?: string }) {
  return (
    <ProjectProvider userId={userId} userEmail={userEmail}>
      <UIProvider>
        <MainAppContent />
      </UIProvider>
    </ProjectProvider>
  );
}

function MainAppContent() {
  const { user } = useAuth();
  const modalRegistry = useModalRegistry();
  const {
    setShowExportProModal,
    setShowCommandPalette,
    setShowQuickAddTaskModal,
    setShowOnboardingWizard,
    setShowDragDropHint,
    setShowExitConfirm,
    setShowBiographyModal,
    isAnyModalOpen,
    closeTopModalOnEscape
  } = modalRegistry;

  const initialActiveNodeRectRef = useRef<ClientRect | null>(null);
  const viewDragHandlersRef = useRef<{
    onDragStart?: (event: DragStartEvent) => void;
    onDragEnd?: (event: DragEndEvent) => void;
    onDragOver?: (event: DragOverEvent) => void;
  } | null>(null);

  const registerViewDragHandlers = useCallback((handlers: {
    onDragStart?: (event: DragStartEvent) => void;
    onDragEnd?: (event: DragEndEvent) => void;
    onDragOver?: (event: DragOverEvent) => void;
  } | null) => {
    viewDragHandlersRef.current = handlers;
  }, []);

  // 1. Consume Contexts
  const projectManager = useProject();
  const {
    tasks, projects, currentProjectId, setCurrentProjectId,
    addTask, updateTask, deleteTask, commitTasks,
    undo, redo, history,
    addLogEntry,
    activeWorkspace, setActiveWorkspace, allProjects, setAllProjects, syncProjectToFirestore,
    updateProjectMetadata,
    allTasksGlobal, globalMetadata, handleAssignTaskToProject,
    isInitialLoadComplete,
    userId: currentUserId
  } = projectManager;

  const { addIdea } = useIdeaPool(currentUserId, activeWorkspace);
  useRecurringTasksScheduler(allProjects, addTask, updateProjectMetadata);

  const appState = useUI();
  const {
    viewMode, setViewMode, isDarkMode, setIsDarkMode,
    uiMode, setUiMode,
    setShowFilters, showFilters,
    showToolsDropdown, setShowToolsDropdown,
    setActiveProjectTab: _setActiveProjectTab,
    isSidebarCollapsed, setIsSidebarCollapsed,
    showDependencies, datePadding, hideEmptyDays,
    showTaskDetails, setShowTaskDetails,
    searchQuery, setSearchQuery,
    setActiveSearchTaskId,
    filterAssignee, setFilterAssignee, filterType, setFilterType,
    filterStatus, setFilterStatus, filterPriority, setFilterPriority,
    editingTask, setEditingTask,
    visibleTasks,
    customAlert
  } = appState;

  const viewTabsState = useAppViewTabsState({
    activeWorkspace,
    setViewMode,
    setActiveProjectTab: _setActiveProjectTab,
    setCurrentProjectId,
    setFilterAssignee,
    setFilterType,
    setFilterStatus,
    setFilterPriority,
    setSearchQuery,
    uiMode,
    setUiMode,
    setShowBiographyModal,
  });

  const {
    activeMetricsTab, setActiveMetricsTab,
    aiAssistantTab, setAiAssistantTab,
    activePoolTab, setActivePoolTab,
    hiddenProjectIds, setHiddenProjectIds,
    isTimeMachineOpen, setIsTimeMachineOpen,
    isRightSidebarOpen, setIsRightSidebarOpen,
    syncProjectViews,
    selectedAnalysisProjectId, setSelectedAnalysisProjectId,
    handleClearAssigneeFilter,
  } = viewTabsState;

  const {
    isMobile,
    showUndo, setShowUndo,
    containerHeight,
    mobileTasks,
    categories,
    isProjectEditor,
    workspaceProjects,
    otherWorkspaceProjects,
    dropdownProjects,
    globalAllAssignees,
    globalAvailableTypes,
    globalAvailableStatuses,
    handleReorderTask,
    handleAddToHistory,
    todayStr,
  } = useAppLifecycleAndSelectors({
    allProjects, projects, allTasksGlobal,
    currentProjectId, currentUserId: currentUserId || '',
    userEmail: user?.email,
    activeWorkspace, globalMetadata,
    isInitialLoadComplete, viewMode,
    isRightSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed,
    setShowOnboardingWizard,
    reorderTask: projectManager.reorderTask,
    updateProjectMetadata,
    historyPastLength: history.past.length,
  });

  const { isProjectView, isConceptView, isStandardTaskView } = useViewNavigationPersistence({
    viewMode, setViewMode,
    currentProjectId, setCurrentProjectId,
    activeWorkspace, setActiveWorkspace,
    activePoolTab, setActivePoolTab,
    hiddenProjectIds, setHiddenProjectIds,
    showTaskDetails, setShowTaskDetails,
    setShowExitConfirm,
    syncProjectViews,
    selectedAnalysisProjectId, setSelectedAnalysisProjectId,
    allProjects, projects, setAllProjects,
  });

  const deferredVisibleTasks = useDeferredValue(visibleTasks);
  const deferredTasks = useDeferredValue(tasks);
  const handleEditTaskMemo = useCallback((task: Task) => {
    setEditingTask({ ...task, projectId: task.projectId || currentProjectId });
  }, [setEditingTask, currentProjectId]);
  const handleEditTaskGlobalMemo = useCallback((_: unknown, task: Task) => {
    setEditingTask(task);
  }, [setEditingTask]);

  const {
    activeDragId, activeDragTask, droppedZoneId, lastDragEndRef,
    handleDragStart, handleDragOver, handleDragEnd, handleDragCancel, customCollisionDetection
  } = useAppDndCoordinator({
    isProjectEditor, allProjects, currentUserId, userEmail: user?.email,
    customAlert, viewDragHandlersRef, setShowDragDropHint,
    allTasksGlobal, handleAssignTaskToProject, updateTask, deleteTask, addLogEntry,
    initialActiveNodeRectRef
  });

  const commDragMeta = useCommDragMeta();

  const handleAddTask = useCallback((type?: Task['type'], parentId?: string | number, projectId?: string, name?: string, initialData?: Partial<Task>, options?: { bypassDragGuard?: boolean }) => {
    if (!options?.bypassDragGuard && (activeDragId || (Date.now() - lastDragEndRef.current < 600))) {
      console.warn('[App] handleAddTask blocked: Drag operation in progress or just ended.');
      return;
    }
    return addTask(type, parentId, projectId, name, initialData, options);
  }, [addTask, activeDragId, lastDragEndRef]);

  useGlobalKeyboardShortcuts({
    setShowCommandPalette, setShowExportProModal, setShowQuickAddTaskModal,
    setShowFilters, closeTopModalOnEscape, isModalOpen: isAnyModalOpen,
    editingTask, setEditingTask, currentProjectId, isProjectEditor, allProjects, currentUserId, userEmail: user?.email,
    deleteTask, updateTask, viewMode, setViewMode,
    activePoolTab, activeMetricsTab, undo, redo
  });

  // 3. Gantt Logic
  const ganttState = useGanttState(
    visibleTasks, tasks, commitTasks,
    showDependencies, datePadding, hideEmptyDays, viewMode
  );
  const { zoomToFit, scrollToTask, editTaskCallbackRef, isFitMode } = ganttState;

  useEffect(() => {
    editTaskCallbackRef.current = (id: string | number) => {
      const task = tasks.find(t => t.id.toString() === id.toString());
      if (task) setEditingTask(task);
    };
  }, [tasks, setEditingTask, editTaskCallbackRef]);

  const timelineRef = useRef<{ scrollToToday: () => void }>(null);

  const {
    peopleWithActiveVorgang, sidebarPeople, sidebarPeopleData,
    handleToggleSidebarPersonFilter, handleOpenSidebarContactCard, handleAddPersonToProject,
  } = useSidebarPeopleController({
    allProjects, allTasksGlobal, tasks,
    currentProjectId, viewMode, activeWorkspace,
    setFilterAssignee, setEditingTask, handleAddTask,
  });

  const isFilterActive = filterAssignee !== 'all' || filterType !== 'all' || filterStatus !== 'all' || filterPriority !== 'all' || searchQuery !== '';

  useLegacyContactHealing({
    isInitialLoadComplete,
    allProjects, setAllProjects, syncProjectToFirestore,
    viewMode, currentProjectId,
    migrateProjectToSortOrder: projectManager.migrateProjectToSortOrder,
  });

  const {
    lastNavigatedIndexRef, attemptScrollAndHighlight,
    navigateToSearchResult, handleSearchNext, handleSearchPrev
  } = useAppSearch({
    scrollToTask, activeMetricsTab, setActiveMetricsTab, aiAssistantTab, setAiAssistantTab,
  });

  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications({
    projects: allProjects,
    userEmail: user?.email || undefined
  });

  const handleNavigateToNotificationTask = useCallback((projectId: string, taskId: string | number) => {
    if (projectId && projectId !== currentProjectId) {
      setCurrentProjectId(projectId);
    }
    setActiveSearchTaskId(taskId);
    attemptScrollAndHighlight(taskId, viewMode);
  }, [currentProjectId, setCurrentProjectId, setActiveSearchTaskId, attemptScrollAndHighlight, viewMode]);

  if (isMobile) {
    return (
      <AppMobileGate
        isInitialLoadComplete={isInitialLoadComplete}
        allProjects={allProjects}
        currentProjectId={currentProjectId}
        setCurrentProjectId={setCurrentProjectId}
        mobileTasks={mobileTasks}
        handleAddTask={handleAddTask}
        updateTask={updateTask}
        deleteTask={deleteTask}
        activeWorkspace={activeWorkspace}
        setActiveWorkspace={setActiveWorkspace}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        projectManager={projectManager}
        globalMetadata={globalMetadata}
        addIdea={addIdea}
      />
    );
  }

  if (!isInitialLoadComplete) {
    return <AppSplashLoader subtitle="Projektdaten werden geladen…" />;
  }

  return (
    <AppDesktopLayout
      customCollisionDetection={customCollisionDetection}
      handleDragStart={handleDragStart}
      handleDragEnd={handleDragEnd}
      handleDragOver={handleDragOver}
      handleDragCancel={handleDragCancel}
      isDarkMode={isDarkMode}
      modalRegistry={modalRegistry}
      viewTabsState={viewTabsState}
      handleSearchPrev={handleSearchPrev}
      handleSearchNext={handleSearchNext}
      navigateToSearchResult={navigateToSearchResult}
      lastNavigatedIndexRef={lastNavigatedIndexRef}
      isFilterActive={isFilterActive}
      showUndo={showUndo}
      setShowUndo={setShowUndo}
      notifications={notifications}
      unreadCount={unreadCount}
      markAsRead={markAsRead}
      markAllAsRead={markAllAsRead}
      handleNavigateToNotificationTask={handleNavigateToNotificationTask}
      viewMode={viewMode}
      isProjectView={isProjectView}
      isConceptView={isConceptView}
      isStandardTaskView={isStandardTaskView}
      isProjectEditor={isProjectEditor}
      dropdownProjects={dropdownProjects}
      globalAllAssignees={globalAllAssignees}
      globalAvailableTypes={globalAvailableTypes}
      globalAvailableStatuses={globalAvailableStatuses}
      showToolsDropdown={showToolsDropdown}
      setShowToolsDropdown={setShowToolsDropdown}
      zoomToFit={zoomToFit}
      isFitMode={isFitMode}
      setAiAssistantTab={setAiAssistantTab}
      workspaceProjects={workspaceProjects}
      showFilters={showFilters}
      isRightSidebarOpen={isRightSidebarOpen}
      isSidebarCollapsed={isSidebarCollapsed}
      sidebarPeople={sidebarPeople}
      sidebarPeopleData={sidebarPeopleData}
      activeDragId={activeDragId}
      droppedZoneId={droppedZoneId}
      filterAssignee={filterAssignee}
      peopleWithActiveVorgang={peopleWithActiveVorgang}
      handleToggleSidebarPersonFilter={handleToggleSidebarPersonFilter}
      handleOpenSidebarContactCard={handleOpenSidebarContactCard}
      handleClearAssigneeFilter={handleClearAssigneeFilter}
      handleAddPersonToProject={handleAddPersonToProject}
      deferredVisibleTasks={deferredVisibleTasks}
      deferredTasks={deferredTasks}
      handleAddTask={handleAddTask}
      addIdea={addIdea}
      ganttState={ganttState}
      todayStr={todayStr}
      containerHeight={containerHeight}
      registerViewDragHandlers={registerViewDragHandlers}
      handleReorderTask={handleReorderTask}
      handleEditTaskMemo={handleEditTaskMemo}
      handleEditTaskGlobalMemo={handleEditTaskGlobalMemo}
      currentUserId={currentUserId}
      userEmail={user?.email || undefined}
      otherWorkspaceProjects={otherWorkspaceProjects}
      handleAddToHistory={handleAddToHistory}
      timelineRef={timelineRef}
      isTimeMachineOpen={isTimeMachineOpen}
      setIsTimeMachineOpen={setIsTimeMachineOpen}
      categories={categories}
      setIsRightSidebarOpen={setIsRightSidebarOpen}
      commDragMeta={commDragMeta}
      activeDragTask={activeDragTask}
      initialActiveNodeRectRef={initialActiveNodeRectRef}
      editingTask={editingTask}
    />
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { user, loading, checking, isAllowed } = useAuth();
  if (loading || checking) {
    return <AppSplashLoader subtitle={user ? 'Berechtigungen werden geprüft…' : 'Wird geladen…'} />;
  }
  if (!user || !isAllowed) {
    return <AppWelcomeGate />;
  }
  return <MainApp userId={user.uid} userEmail={user.email || undefined} />;
}
